import React, { Fragment, useMemo } from 'react';
import Link from 'shared/SlashLink';
import projects from '../../data/ca_housing_pipeline.json';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import PipelineExplorer from '../../components/PipelineExplorer/PipelineExplorer';
import PipelineCTA from '../../components/PipelineCTA/PipelineCTA';
import { doorFeeIncome } from '../../data/doorFee';

const money = (n) => (n >= 1e6 ? '$' + (n / 1e6).toFixed(1) + 'M' : '$' + Math.round(n / 1e3) + 'K');

const HousingPipelinePage = () => {
    usePageSeo({
        title: 'California Affordable Housing Pipeline — New LIHTC Projects',
        description: 'Searchable directory of new California affordable housing projects funded with 9% and 4% LIHTC — builders, unit counts, housing type, and county, from CTCAC data.',
        keywords: 'California affordable housing pipeline, LIHTC projects, 9% tax credit, 4% tax credit, CTCAC, affordable housing developers, new construction multifamily, senior housing California',
    });

    const stats = useMemo(() => {
        const counties = {};
        let units = 0, nine = 0, four = 0, doorFeeTotal = 0;
        projects.forEach((p) => {
            counties[p.county] = (counties[p.county] || 0) + 1;
            units += p.totalUnits || 0;
            doorFeeTotal += doorFeeIncome(p.totalUnits);
            if (p.creditType === '9%') nine++; else if (p.creditType === '4%') four++;
        });
        const topCounties = Object.entries(counties).sort((a, b) => b[1] - a[1]);
        const types = {};
        projects.forEach((p) => {
            if (!types[p.housingType]) types[p.housingType] = { count: 0, slug: p.housingTypeSlug };
            types[p.housingType].count += 1;
        });
        const housingTypes = Object.entries(types).sort((a, b) => b[1].count - a[1].count);
        const devs = {};
        projects.forEach((p) => {
            if (!p.developer) return;
            if (!devs[p.developer]) devs[p.developer] = { count: 0, slug: p.developerSlug };
            devs[p.developer].count += 1;
        });
        const topDevelopers = Object.entries(devs).sort((a, b) => b[1].count - a[1].count).slice(0, 16);
        return { total: projects.length, units, nine, four, doorFeeTotal, countyCount: Object.keys(counties).length, topCounties, housingTypes, topDevelopers };
    }, []);

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'California Affordable Housing Pipeline',
        numberOfItems: stats.total,
        itemListElement: projects.slice(0, 50).map((p, i) => ({
            '@type': 'ListItem', position: i + 1, name: p.name,
        })),
    };

    const StatCard = ({ value, label }) => (
        <div className="col-6 col-lg mb-3">
            <div className="text-center p-3 h-100" style={{ background: '#fff', border: '1px solid #ead9cd', borderRadius: 14 }}>
                <div className="h2 mb-0" style={{ color: '#B85C38' }}>{value}</div>
                <div className="small text-muted">{label}</div>
            </div>
        </div>
    );

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'California Affordable Housing Pipeline'} pagesub={'Pipeline'} pageTop={'Affordable Housing'} />
                <section className="section_space bg-light">
                    <div className="container">
                        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">New affordable housing projects across California</h2>
                                <p>
                                    Explore affordable housing developments in the California pipeline — projects awarded
                                    <strong> 9% and 4% Low-Income Housing Tax Credits (LIHTC)</strong> through the California Tax
                                    Credit Allocation Committee (CTCAC). Search by builder, filter by county, housing type, or
                                    credit type, and see exactly where each development sits on the map.
                                </p>
                                <p className="text-muted">
                                    BUILDLAB designs the low-voltage and life-safety systems behind projects like these —
                                    structured cabling, surveillance, door access, and fire &amp; life safety, all engineered
                                    in-house. This directory is built from public CTCAC data; figures reflect the most recent
                                    award rounds.
                                </p>
                            </div>
                        </div>

                        <div className="row mb-5">
                            <StatCard value={stats.total} label="Pipeline projects" />
                            <StatCard value={stats.units.toLocaleString()} label="Total units" />
                            <StatCard value={money(stats.doorFeeTotal)} label="Potential door-fee income" />
                            <StatCard value={`${stats.nine} / ${stats.four}`} label="9% / 4% credit" />
                            <StatCard value={stats.countyCount} label="Counties" />
                        </div>

                        <PipelineExplorer projects={projects} showCountyFilter={true} />

                        <PipelineCTA />

                        {/* SEO internal links to housing-type pages */}
                        <div className="mt-5">
                            <h2 className="h4 mb-3">Browse the pipeline by housing type</h2>
                            <div className="row">
                                {stats.housingTypes.map(([name, { count, slug }]) => (
                                    <div className="col-lg-3 col-md-4 col-6 mb-2" key={name}>
                                        <Link to={`/housing-pipeline/type/${slug}`} className="d-block">
                                            {name} <span className="text-muted">({count})</span>
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* SEO internal links to developer pages */}
                        <div className="mt-5">
                            <h2 className="h4 mb-3">Browse the pipeline by developer</h2>
                            <div className="row">
                                {stats.topDevelopers.map(([name, { count, slug }]) => (
                                    <div className="col-lg-4 col-md-6 mb-2" key={slug}>
                                        <Link to={`/housing-pipeline/developer/${slug}`} className="d-block text-truncate">
                                            {name} <span className="text-muted">({count})</span>
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* SEO internal links to county pages */}
                        <div className="mt-5">
                            <h2 className="h4 mb-3">Browse the pipeline by county</h2>
                            <div className="row">
                                {stats.topCounties.map(([name, count]) => {
                                    const slug = name.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
                                    return (
                                        <div className="col-lg-3 col-md-4 col-6 mb-2" key={name}>
                                            <Link to={`/housing-pipeline/county/${slug}`} className="d-block">
                                                {name} County <span className="text-muted">({count})</span>
                                            </Link>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="small text-muted mt-3">
                                Data source: California Tax Credit Allocation Committee (CTCAC). Map &copy; OpenStreetMap contributors.
                            </p>
                        </div>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default HousingPipelinePage;
